// =============================================================================
// src/data/ai-surfaces/index.ts — Barrel + helpers do registro de superfícies de IA.
//
// Fonte ÚNICA do mapa de IA: agrega os 5 domínios, expõe lookup por id e
//   agrupamento por domínio. Lido pelo badge (src/views/shared/ai-badge.ts), pela
//   rota /mapa-ia (src/features/mapa-ia/**) e pelo gerador da AI-CONTRACT.md.
// =============================================================================

import type { AiSurfaceDomain, AiSurfaceEntry } from '../ai-surface-types';
import { COPILOT_AI_SURFACES } from './copilot-surfaces';
import { CRISIS_AI_SURFACES } from './crisis-surfaces';
import { REPUTATION_AI_SURFACES } from './reputation-surfaces';
import { MONITORING_AI_SURFACES } from './monitoring-surfaces';
import { SECTION_INSIGHT_AI_SURFACES } from './section-insight-surfaces';

/** Todas as superfícies de IA do front, na ordem dos domínios. */
export const AI_SURFACES: readonly AiSurfaceEntry[] = [
  ...COPILOT_AI_SURFACES,
  ...CRISIS_AI_SURFACES,
  ...REPUTATION_AI_SURFACES,
  ...MONITORING_AI_SURFACES,
  ...SECTION_INSIGHT_AI_SURFACES,
];

/** Ordem canônica dos domínios no /mapa-ia e na AI-CONTRACT. */
export const AI_SURFACE_DOMAIN_ORDER: readonly AiSurfaceDomain[] = [
  'copiloto',
  'crise',
  'reputacao',
  'monitoramento',
  'section-insight',
];

/** Rótulo humano de cada domínio. */
export const AI_SURFACE_DOMAIN_LABEL: Record<AiSurfaceDomain, string> = {
  copiloto: 'Copiloto IA',
  crise: 'Gestão de Crise',
  reputacao: 'Gestão de Reputação',
  monitoramento: 'Monitoramento',
  'section-insight': 'Insights por seção',
};

/** Busca uma superfície pelo id; undefined se inexistente. */
export function aiSurfaceById(id: string): AiSurfaceEntry | undefined {
  return AI_SURFACES.find((surface) => surface.id === id);
}

/** Grupo de superfícies de um domínio (DRY para o painel /mapa-ia e a doc). */
export interface AiSurfaceGroup {
  readonly domain: AiSurfaceDomain;
  readonly label: string;
  readonly surfaces: readonly AiSurfaceEntry[];
}

/** Agrupa AI_SURFACES por domínio, na ordem canônica. */
export function groupAiSurfacesByDomain(): readonly AiSurfaceGroup[] {
  return AI_SURFACE_DOMAIN_ORDER.map((domain) => ({
    domain,
    label: AI_SURFACE_DOMAIN_LABEL[domain],
    surfaces: AI_SURFACES.filter((surface) => surface.domain === domain),
  }));
}
